/* JDMuscle delivery estimate (replaces the Omega Estimated Delivery app, 2026-10-24).
 * In-stock cutoff countdown (2026-10-25): "Order within 2h 14m to ship today." only when in stock, on a business day, before 3 PM ET; minute updates, PDP only (never in the cart).
 * No availability / ship-by text here (Leo 2026-10-25): the theme's inventory status line above is the single place for availability and ship date.
 * Lines: countdown (in stock before the cutoff only), delivery window (default or by ZIP), ZIP note, and a cart line-item property. No free-shipping line (Leo 2026-10-25): shipping cost appears only after a ZIP, from jdm-shipping-quote.
 * Date math, 3 PM ET cutoff, holiday calendar (shop metafield jdmuscle.holiday_calendar, US federal fallback) and lead-time rules come from
 * assets/jdm-pickup-eta.js (window.JdmPickupEta) so the pickup line and the delivery line always agree. */
(function (root) {
  'use strict';
  const THEME = String(root.Shopify?.theme?.id);
  const DEFAULT_TRANSIT = [1, 5];
  const TAIWAN_TRANSIT = [3, 7];
  // Vendor rules kept in assets/jdm-fulfillment-policy.js print their own delivery copy; our window yields to them.
  const VENDOR_ALWAYS = ['Idoing', 'Dmann Tuning'];
  const VENDOR_IN_STOCK = ['Seibon', 'Tomei', 'PRL Motorsports', 'GrimmSpeed', 'STM Tuned', 'STM Tuned Inc.', 'EOS', 'Extreme Turbo Systems', 'MAPerformance', 'IAG Performance'];
  // ZIP3 -> business days in transit from Willow Grove, PA 19090 (ground). [from, to, min, max]
  const ZONES = [
    [10, 69, 2, 3], [70, 89, 1, 2], [100, 199, 1, 2], [200, 219, 1, 2], [220, 299, 2, 3],
    [300, 319, 2, 3], [320, 349, 3, 4], [350, 369, 3, 4], [370, 385, 2, 3], [386, 397, 3, 4], [398, 399, 2, 3],
    [400, 499, 2, 3], [500, 569, 3, 4], [570, 599, 4, 5], [600, 629, 2, 3], [630, 699, 3, 4],
    [700, 749, 3, 4], [750, 799, 3, 5], [800, 961, 4, 5], [970, 994, 4, 5]
  ];
  const UNSUPPORTED = [[0, 9], [90, 99], [340, 340], [962, 969], [995, 999]];

  /** Pure: ZIP -> { kind: 'none' | 'invalid' | 'unsupported' | 'ok', zip, min, max } */
  function transitForZip(raw) {
    const zip = String(raw == null ? '' : raw).trim();
    if (!zip) return { kind: 'none' };
    if (!/^\d{5}$/.test(zip)) return { kind: 'invalid', zip };
    const z3 = Number(zip.slice(0, 3));
    if (UNSUPPORTED.some(([a, b]) => z3 >= a && z3 <= b)) return { kind: 'unsupported', zip };
    const hit = ZONES.find(([a, b]) => z3 >= a && z3 <= b);
    return hit ? { kind: 'ok', zip, min: hit[2], max: hit[3] } : { kind: 'unsupported', zip };
  }

  const shortDate = (s) => new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' }).format(new Date(s + 'T00:00:00Z'));
  const shortDateYear = (s) => new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(s + 'T00:00:00Z'));

  const monthYear = (s) => new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'long', year: 'numeric' }).format(new Date(s + 'T00:00:00Z'));
  const monthOnly = (s) => new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'long' }).format(new Date(s + 'T00:00:00Z'));
  /** Pure: delivery window in months only (> 90 days out): 'April 2027' / 'March – April 2027' / 'December 2026 – January 2027'. */
  const monthRange = (a, b) => (a.slice(0, 7) === b.slice(0, 7) ? monthYear(a) : a.slice(0, 4) === b.slice(0, 4) ? `${monthOnly(a)} – ${monthYear(b)}` : `${monthYear(a)} – ${monthYear(b)}`);

  /** Pure: minutes left -> '2h 14m' / '45m' (no seconds). */
  const fmtLeft = (m) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);

  /** Pure: everything the delivery block shows. src = jdm-willow-pickup-state JSON; api = JdmPickupEta. */
  function deliveryPlan(src, now, opts, api) {
    const qty = Number.isInteger(opts?.quantity) && opts.quantity > 0 ? opts.quantity : 1;
    const t = transitForZip(opts?.zip);
    const out = { countdown: null, avail: null, delivery: null, taiwan: null, zipNote: null, prop: null, hidden: false, mode: null, ship: null };
    if (!src || !api) return { ...out, hidden: true, mode: 'no-source' };
    const vendor = String(src.vendor || '');
    const tracked = Number.isInteger(src.total);
    const deny = src.policy === 'deny';
    const inStock = deny ? src.available === true && (!tracked || src.total >= qty) : tracked && src.total >= qty;
    if (deny && !inStock) return { ...out, hidden: true, mode: 'sold-out' };
    if (VENDOR_ALWAYS.includes(vendor) || (inStock && VENDOR_IN_STOCK.includes(vendor))) return { ...out, mode: 'vendor' };
    const { date: today, minutes } = api.localNow(now, api.CFG.TZ);
    const processing = api.isBusinessDay(today) && minutes < api.CFG.CUTOFF_HOUR * 60 ? today : api.nextBusinessDay(today);
    let ship = null, long = false;
    if (inStock) {
      ship = processing; out.mode = 'in-stock';
      const left = api.CFG.CUTOFF_HOUR * 60 - minutes;
      // Countdown only (Leo 2026-10-25): no "In stock" / "Ships <day>" here; the theme status line already says it.
      if (processing === today && left > 0) {
        out.countdown = left;
        out.avail = `Order within ${fmtLeft(left)} to ship today.`;
      }
    } else if (!tracked && !(src.eta || src.leadDays > 0)) {
      return { ...out, mode: 'untracked' };
    } else {
      const est = api.pickupEstimate({ ...src, state: 'eta' }, now);
      if (!est || !est.date) return { ...out, mode: 'unknown' };
      ship = est.date; long = est.kind === 'long'; out.mode = long ? 'long' : 'date';
      // No "Available to order / Special order. Estimated to ship by …" line (Leo 2026-10-25): it duplicated the theme status line.
    }
    out.ship = ship;   // internal (tests / debugging); never printed
    const taiwan = !inStock && src.taiwan === true;
    const [min, max] = taiwan ? TAIWAN_TRANSIT : t.kind === 'ok' ? [t.min, t.max] : DEFAULT_TRANSIT;
    const where = t.kind === 'ok' ? ` to ${t.zip}` : '';
    // Taiwan items keep their 3–7 day transit in the window; the separate "Ships directly from our Taiwan warehouse." line is dropped (delivery timeframe only).
    if (t.kind === 'unsupported') out.zipNote = `Delivery time to ${t.zip} is confirmed at checkout.`;
    const first = api.addBusinessDays(ship, min), last = api.addBusinessDays(ship, max);
    if (long) {
      // More than 90 days out: month(s) only.
      out.delivery = `Estimated delivery${where}: ${monthRange(first, last)}`;
      out.prop = { name: 'Estimated ship date', value: api.fmtMonth(ship) };
    } else {
      out.delivery = `Estimated delivery${where}: ${api.fmtDay(first)} – ${api.fmtDay(last)}`;
      out.prop = inStock ? { name: 'Estimated delivery', value: `${shortDate(first)} – ${shortDate(last)}` } : { name: 'Estimated ship date', value: shortDateYear(ship) };
    }
    return out;
  }

  const api = { transitForZip, deliveryPlan, fmtLeft, monthRange, ZONES, UNSUPPORTED };
  if (typeof module === 'object' && module.exports) module.exports = api;
  root.JdmDelivery = api;
  if (!root.customElements || root.customElements.get('jdm-delivery')) return;

  const SESSION_KEY = 'jdm-shipping-destination-v1';
  const PROP_NAMES = ['Estimated delivery', 'Estimated ship date'];
  class JdmDeliveryElement extends root.HTMLElement {
    connectedCallback() {
      if (this.ready || this.dataset.themeId !== THEME || String(root.Shopify?.theme?.id) !== THEME || !['main', 'unpublished'].includes(root.Shopify?.theme?.role)) return;
      this.scope = this.closest('product-info');
      this.form = root.document.getElementById(this.dataset.form);
      if (!this.scope || !this.form) return;
      this.el = {
        avail: this.querySelector('[data-delivery-avail]'), taiwan: this.querySelector('[data-delivery-taiwan]'),
        delivery: this.querySelector('[data-delivery-window]'), zipNote: this.querySelector('[data-delivery-zip-note]'),
      };
      this.ready = true; this.cache = new Map(); this.zip = this.storedZip();
      try { const s = JSON.parse(this.querySelector('[data-delivery-source]')?.textContent || 'null'); if (s && s.v === 1) this.cache.set(String(s.variantId), s); } catch {}
      this.ctrl = new root.AbortController();
      const on = (t, n, f, o) => t.addEventListener(n, f, { signal: this.ctrl.signal, ...(o || {}) });
      on(this.scope, 'jdm:variant-source', (e) => { const s = e.detail; if (s && s.v === 1) { this.cache.set(String(s.variantId), s); this.render(); } });
      on(this.scope, 'change', (e) => { if (e.target.closest?.('variant-selects') || e.target.matches?.('[name="id"]') || e.target.matches?.('[name="quantity"]')) root.setTimeout(() => this.render(), 0); });
      // Capture phase: jdm-shipping-quote calls stopPropagation() on its Check/Retry clicks and Enter key.
      on(this.scope, 'change', (e) => { if (e.target.matches?.('[data-quote-zip]')) this.takeZip(e.target.value); }, { capture: true });
      on(this.scope, 'input', (e) => { if (e.target.matches?.('[name="quantity"]')) this.render(); });
      on(this.scope, 'click', (e) => { if (e.target.closest?.('[data-quote-check], [data-quote-retry]')) this.takeZip(this.scope.querySelector('[data-quote-zip]')?.value); }, { capture: true });
      on(this.scope, 'keydown', (e) => { if (e.key === 'Enter' && e.target.matches?.('[data-quote-zip]')) this.takeZip(e.target.value); }, { capture: true });
      on(root.document, 'visibilitychange', () => { if (root.document.visibilityState === 'visible') this.render(); });
      on(root, 'pageshow', () => this.render());
      on(this.form, 'submit', () => this.syncProperty(), { capture: true });
      // Re-render on each minute boundary so the countdown ticks per minute and flips to "Ships <next business day>" at 3:00 PM ET without a reload.
      const tick = () => { this.render(); this.timer = root.setTimeout(tick, 60000 - (Date.now() % 60000) + 50); };
      this.timer = root.setTimeout(tick, 60000 - (Date.now() % 60000) + 50);
      // assets/jdm-pickup-eta.js (date engine) may load after this file; all deferred scripts have run by DOMContentLoaded.
      if (!root.JdmPickupEta && root.document.readyState === 'loading') on(root.document, 'DOMContentLoaded', () => this.render(), { once: true });
      this.render();
    }
    disconnectedCallback() { this.ctrl?.abort(); root.clearTimeout(this.timer); this.ready = false; }
    storedZip() {
      try { const v = JSON.parse(root.sessionStorage.getItem(SESSION_KEY) || 'null'); if (v && /^\d{5}$/.test(String(v.zip))) return String(v.zip); } catch {}
      const c = this.scope?.querySelector('jdm-shipping-quote')?.dataset.customerZip;
      return c && /^\d{5}/.test(c) ? c.slice(0, 5) : '';
    }
    takeZip(v) { this.zip = String(v || '').trim(); this.render(); }
    variantId() { return String(this.form.querySelector('[name="id"]')?.value || ''); }
    quantity() { const q = [...root.document.querySelectorAll('[name="quantity"]')].find((n) => n.form === this.form || n.getAttribute('form') === this.form.id); const n = q ? Number(q.value) : 1; return Number.isInteger(n) && n > 0 ? n : 1; }
    plan() {
      const src = this.cache.get(this.variantId());
      return deliveryPlan(src, Date.now(), { zip: this.zip, quantity: this.quantity() }, root.JdmPickupEta);
    }
    render() {
      if (!this.ready) return;
      const p = this.plan();
      this.current = p;
      const set = (el, text) => { if (!el) return; if (el.textContent !== (text || '')) el.textContent = text || ''; el.hidden = !text; };
      set(this.el.avail, p.avail); set(this.el.taiwan, p.taiwan); set(this.el.delivery, p.delivery); set(this.el.zipNote, p.zipNote);
      if (this.el.avail) { if (p.countdown) this.el.avail.setAttribute('data-delivery-countdown', String(p.countdown)); else this.el.avail.removeAttribute('data-delivery-countdown'); }
      this.hidden = p.hidden || !(p.avail || p.taiwan || p.delivery || p.zipNote);
      this.syncProperty();
    }
    syncProperty() {
      if (!this.form) return;
      const p = this.current || this.plan();
      for (const name of PROP_NAMES) {
        const input = this.form.querySelector(`input[type="hidden"][name="properties[${name}]"][data-jdm-delivery-prop]`);
        if (p.prop && p.prop.name === name) {
          const node = input || Object.assign(root.document.createElement('input'), { type: 'hidden', name: `properties[${name}]` });
          node.setAttribute('data-jdm-delivery-prop', '');
          if (node.value !== p.prop.value) node.value = p.prop.value;
          if (!input) this.form.appendChild(node);
        } else if (input) input.remove();
      }
    }
  }
  root.customElements.define('jdm-delivery', JdmDeliveryElement);
})(typeof window === 'object' ? window : globalThis);
